/**
 * Lightweight internal analytics tracker for Bloom Lingerie.
 * Tracks: page_view, product_view, add_to_cart, checkout_start, purchase, cart_abandon, language_switch
 * Also fans out to Meta Pixel (when loaded) for retargeting + conversion tracking.
 */
const API_URL = '';

// ── Meta Pixel fan-out (no-op when pixel not loaded) ──────────
const _fbq = (...args) => {
  if (typeof window !== 'undefined' && window.fbq) {
    try { window.fbq(...args); } catch { /* swallow */ }
  }
};

function getSessionId() {
  let sid = sessionStorage.getItem('bloom_sid');
  if (!sid) {
    sid = `s_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    sessionStorage.setItem('bloom_sid', sid);
  }
  return sid;
}

function getDevice() {
  const w = window.innerWidth;
  if (w < 768) return 'mobile';
  if (w < 1024) return 'tablet';
  return 'desktop';
}

function getUTM() {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source') || 'direct',
    utm_medium: params.get('utm_medium') || '',
    utm_campaign: params.get('utm_campaign') || '',
  };
}

export function trackEvent(event, data = {}) {
  try {
    const payload = {
      event,
      session_id: getSessionId(),
      data: {
        ...data,
        device: getDevice(),
        language: localStorage.getItem('bloom_lang') || 'fr',
        ...getUTM(),
      },
      timestamp: new Date().toISOString(),
    };

    if (navigator.sendBeacon) {
      navigator.sendBeacon(
        `${API_URL}/api/track`,
        new Blob([JSON.stringify(payload)], { type: 'application/json' })
      );
    } else {
      fetch(`${API_URL}/api/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {});
    }
  } catch (e) {
    // Silent fail — tracking should never break the site
  }
}

// ── Internal tracker + Meta Pixel fan-out helpers ─────────────
export const trackPageView = (url) => {
  trackEvent('page_view', { url });
  _fbq('track', 'PageView');
};

export const trackProductView = (productId, name, price, category) => {
  trackEvent('product_view', { product_id: productId, name, price });
  _fbq('track', 'ViewContent', {
    content_ids: [productId],
    content_type: 'product',
    content_name: name,
    content_category: category || '',
    value: Number(price) || 0,
    currency: 'DZD',
  });
};

export const trackAddToCart = (productId, name, size, color, price) => {
  trackEvent('add_to_cart', { product_id: productId, name, size, color, price });
  _fbq('track', 'AddToCart', {
    content_ids: [productId],
    content_type: 'product',
    content_name: name,
    value: Number(price) || 0,
    currency: 'DZD',
  });
};

export const trackCheckoutStart = (total, itemsCount, contentIds = []) => {
  trackEvent('checkout_start', { total, items_count: itemsCount });
  _fbq('track', 'InitiateCheckout', {
    content_ids: contentIds,
    num_items: itemsCount,
    value: Number(total) || 0,
    currency: 'DZD',
  });
};

export const trackPurchase = (orderId, total, wilaya, deliveryType, contents = []) => {
  trackEvent('purchase', { order_id: orderId, total, wilaya, delivery_type: deliveryType });
  _fbq(
    'track',
    'Purchase',
    {
      value: Number(total) || 0,
      currency: 'DZD',
      content_type: 'product',
      contents,
      num_items: contents.reduce((n, c) => n + (Number(c.quantity) || 0), 0),
      order_id: String(orderId),
    },
    { eventID: `purchase_${orderId}` }
  );
};

export const trackCartAbandon = (products) => trackEvent('cart_abandon', { products });
export const trackLanguageSwitch = (from, to) => trackEvent('language_switch', { from, to });
